/**
 * A compact "pick several" filter for the due list's filter bar: a button that
 * says what is chosen, opening a checkbox list. Nothing chosen means no
 * filter. Closes on Escape or a click outside.
 */

import { useEffect, useId, useRef, useState } from 'react'
import type { FilterOption } from '../due-list'

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  /** "Type", "Status" — the button reads "Type: All" or "Type: 2 of 5". */
  label: string
  options: FilterOption[]
  selected: string[]
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const chosen = new Set(selected)
  // A value chosen in the URL but absent from the loaded rows still counts as
  // chosen: it is filtering, so the button must not claim "All".
  const summary =
    selected.length === 0
      ? 'All'
      : selected.length === 1
        ? optionLabel(options, selected[0]!)
        : `${selected.length} selected`

  const toggle = (value: string) => {
    onChange(chosen.has(value) ? selected.filter((v) => v !== value) : [...selected, value])
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={[
          'eq-input-sm flex items-center gap-1.5 whitespace-nowrap',
          selected.length > 0 ? 'border-eq-deep text-eq-deep' : '',
        ].join(' ')}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(!open)}
      >
        <span className="text-eq-neutral">{label}:</span>
        <span className="max-w-[12rem] truncate font-semibold">{summary}</span>
        <span aria-hidden className="text-eq-helper">
          ▾
        </span>
      </button>
      {open && (
        <div
          id={listId}
          role="group"
          aria-label={label}
          className="absolute left-0 top-full z-30 mt-1 max-h-[22rem] min-w-[20rem] overflow-auto rounded-eq-md border border-eq-border bg-white py-1 shadow-eq-raised"
        >
          {options.length === 0 && (
            <div className="px-3 py-2 text-[12.5px] text-eq-helper">Nothing to choose from in this view.</div>
          )}
          {options.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] hover:bg-eq-ice"
            >
              <input type="checkbox" checked={chosen.has(option.value)} onChange={() => toggle(option.value)} />
              <span className="flex-1 whitespace-nowrap">
                {option.label}
                {option.hint && <span className="ml-1.5 text-[11.5px] text-eq-helper">{option.hint}</span>}
              </span>
              <span className="tabular-nums text-[12px] text-eq-helper">{option.count}</span>
            </label>
          ))}
          {selected.length > 0 && (
            <div className="border-t border-eq-border-subtle px-3 pt-1.5 pb-1">
              <button type="button" className="text-[12.5px] text-eq-deep underline" onClick={() => onChange([])}>
                Clear {label.toLowerCase()} filter
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function optionLabel(options: FilterOption[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value
}
